import React, { useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import './TreatmentDigitizer.css';
import { API_BASE_URL, CA_API, API_KEY } from '../config';
import { useAuth } from '../contexts/AuthContext';

// const API_BASE_URL = 'http://localhost:3001';

function TreatmentDigitizer() {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [farms, setFarms] = useState([]);
  const [selectedFarm, setSelectedFarm] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [s3ImageUrl, setS3ImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [caGenerating, setCAGenerating] = useState(false);

  // Search farms
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setError('Please enter a herd ID or farm name');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/farms/search`, {
        params: { q: searchQuery },
        withCredentials: true
      });

      // Backend returns { success: true, farms: [...] }
      const farmsData = response.data.farms || [];
      setFarms(farmsData);

      if (farmsData.length === 0) {
        setError('No farms found matching your search');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error searching farms');
    } finally {
      setLoading(false);
    }
  };

  // Handle farm selection
  const handleSelectFarm = (farm) => {
    setSelectedFarm(farm);
    setFarms([]);
    setSearchQuery('');
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setImagePreview(URL.createObjectURL(file));
      setExtractedData(null);
      setError(null);
    }
  };

  // Process image
  const handleProcessImage = async () => {
    if (!selectedFile) {
      setError('Please select a file.');
      return;
    }

    if (!selectedFarm) {
      setError('Please select a farm first.');
      return;
    }

    // ✅ Client-side validation
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('File too large. Max size is 10MB.');
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png'];  // 'application/pdf'
    if (!allowedTypes.includes(selectedFile.type)) {
      setError('Unsupported file type. Please upload JPG or PNG types.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const formData = new FormData();
      formData.append('image', selectedFile);
      formData.append('herdId', selectedFarm.herd_id);

      const response = await axios.post(
        `${API_BASE_URL}/process-treatment-card`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
          timeout: 120000
        }
      );

      setExtractedData(response.data.data || response.data);
      setS3ImageUrl(response.data.s3ImageUrl);

    } catch (err) {
      let message = 'Something went wrong.';

      if (err.code === 'ECONNABORTED') {
        message = 'Request timed out. Try a smaller file or check your connection.';
      } else if (err.response) {
        message = err.response.data?.message || message;
      } else if (err.request) {
        message = 'Server not responding. Please try again.';
      }

      setError(message);

    } finally {
      setLoading(false);
    }
};
  // Update treatment data
  const handleUpdateTreatment = async (cowIndex, field, value) => {
    const updated = { ...extractedData };
    updated.treatments[cowIndex][field] = value;
    setExtractedData(updated);

    // If cow number or date was changed, re-match to find the correct life number
    if ((field === 'cowNumber' || field === 'date') && value && value.trim() !== '') {
      try {
        const treatment = updated.treatments[cowIndex];

        // Use updated values (in case both were just changed)
        const cowNumberToMatch = field === 'cowNumber' ? value : treatment.cowNumber;
        const dateToMatch = field === 'date' ? value : treatment.date;

        if (!cowNumberToMatch || !dateToMatch) {
          return; // Can't match without both cow number and date
        }

        const response = await axios.post(`${API_BASE_URL}/match-cow`, {
          cowNumber: cowNumberToMatch,
          treatmentDate: dateToMatch,
          herdId: selectedFarm.herd_id
        }, {
          withCredentials: true
        });

        if (response.data.success && response.data.match) {
          // Update with the new matched data
          updated.treatments[cowIndex] = {
            ...updated.treatments[cowIndex],
            lifeNumber: response.data.match.lifenumber,
            calvingDate: response.data.match.calving_date,
            matchStatus: 'matched'
          };
          setExtractedData({ ...updated });
        } else {
          // No match found
          updated.treatments[cowIndex].lifeNumber = null;
          updated.treatments[cowIndex].matchStatus = 'not-found';
          setExtractedData({ ...updated });
        }
      } catch (err) {
        console.error('Error re-matching cow:', err);
      }
    }
  };

  // Update product mark
  const handleUpdateProductMark = (cowIndex, productIndex, value) => {
    const updated = { ...extractedData };
    updated.treatments[cowIndex].productMarks[productIndex].is_marked = value;
    setExtractedData(updated);
  };

  // Add new blank treatment row
  const handleAddTreatment = () => {
    const updated = { ...extractedData };
    const blankTreatment = {
      cowNumber: '',
      date: '',
      observations: '',
      matchStatus: 'not-found',
      lifeNumber: null,
      calvingDate: null,
      productMarks: (extractedData.treatments[0]?.productMarks || []).map(mark => ({
        product_name: mark.product_name,
        is_marked: false
      }))
    };
    updated.treatments.push(blankTreatment);
    setExtractedData(updated);
  };

  // Save data
  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      await axios.post(`${API_BASE_URL}/save-treatment-data`, {
        herdId: selectedFarm.herd_id,
        farmName: selectedFarm.farm_name,
        country: selectedFarm.country,
        treatments: extractedData.treatments,
        s3ImageUrl: s3ImageUrl // Send S3 URL to be saved in database
      }, {
        withCredentials: true
      });

      // Show success message as toast
      toast.success('Treatment data saved successfully!');
      // setSuccessMessage('Treatment data saved successfully!');

      // Reset form after 2 seconds
      setTimeout(() => {
        setSelectedFile(null);
        setImagePreview(null);
        setExtractedData(null);
        setS3ImageUrl(null);
        setSuccessMessage(null);
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving data');
    } finally {
      setLoading(false);
    }
  };

  // Generate CA certificate
  const handleCAGenerate = async () => {
    if (!selectedFarm || !user) {
      setError('Please select a farm and ensure you are logged in');
      return;
    }

    try {
      setCAGenerating(true);
      setError(null);

      const payload = {
        farm_id: selectedFarm.herd_id,
        farm_name: selectedFarm.farm_name,
        country: selectedFarm.country,
        username: user.fullName || user.email || 'Unknown',
        useremail: user.email
      };

      console.log("Current API URL:", CA_API);
      const response = await axios.post(CA_API, payload, {
        withCredentials: true,
        headers: { 'x-api-key': API_KEY}
      });

      if (response.data.success || response.status === 200) {
        toast.success('Report generation started. Check your inbox in about 5 minutes!');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Error generating CA';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setCAGenerating(false);
    }
  };

  return (
    <div className="treatment-digitizer">
      <h1>Treatment Digitizer</h1>

      {/* Farm Selection */}
      <div className="farm-selection-section">
        <h2>Step 1: Select Farm</h2>

        {!selectedFarm ? (
          <div className="farm-search">
            <div className="search-input-group">
              <input
                type="text"
                placeholder="Enter herd ID or farm name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <button onClick={handleSearch} disabled={loading}>
                {loading ? 'Searching...' : 'Search'}
              </button>
            </div>

            {farms.length > 0 && (
              <div className="farm-results">
                <h3>Select a farm:</h3>
                {farms.map(farm => (
                  <div
                    key={farm.herd_id}
                    className="farm-card"
                    onClick={() => handleSelectFarm(farm)}
                  >
                    <div className="farm-info">
                      <strong>{farm.farm_name}</strong>
                      <span>Herd ID: {farm.herd_id}</span>
                      <span>Country: {farm.country || 'N/A'}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className="selected-farm">
              <div className="farm-details">
                <h3>{selectedFarm.farm_name}</h3>
                <p>Herd ID: {selectedFarm.herd_id}</p>
                <p>Country: {selectedFarm.country || 'N/A'}</p>
              </div>
              <button
                className="change-farm-btn"
                onClick={() => {
                  setSelectedFarm(null);
                  setSelectedFile(null);
                  setImagePreview(null);
                  setExtractedData(null);
                }}
              >
                Change Farm
              </button>
            </div>
            <div className="farm-actions">
              <button
                className="ca-generate-btn"
                onClick={handleCAGenerate}
                disabled={caGenerating}
              >
                {caGenerating ? 'Generating...' : 'CA Generate'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Image Upload */}
      {selectedFarm && (
        <div className="image-upload-section">
          <h2>Step 2: Upload Treatment Card Image</h2>
	  <p style={{fontFamily: 'sans-serif', color: '#4a4a4a'}}>
  		Accepted files: <span style={{color: '#007bff', fontWeight: 'bold'}}>PNG, JPEG, and JPG</span>.
	  </p>
          <div className="upload-controls">
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              id="image-upload"
            />
            <label htmlFor="image-upload" className="upload-label">
              {selectedFile ? 'Change Image' : 'Choose Image'}
            </label>
            {selectedFile && (
              <button onClick={handleProcessImage} disabled={loading}>
                {loading ? 'Processing...' : 'Process Image'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Error and Success Messages */}
      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {/* Image Preview (shown immediately after upload) */}
      {imagePreview && !extractedData && (
        <div className="image-preview-only">
          <h2>Uploaded Image</h2>
          <img src={imagePreview} alt="Treatment card" style={{ maxWidth: '100%', borderRadius: '8px', boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }} />
        </div>
      )}

      {/* Results */}
      {imagePreview && extractedData && (
        <div className="results-section">
          <h2>Step 3: Verify and Edit Data</h2>
          <p className="results-instructions">
            Compare each treatment card below with the image on the left. Scroll to verify all treatments.
          </p>

          <div className="results-layout-sidebyside">
            {/* Image Preview - Fixed on left */}
            <div className="image-preview-column">
              <div className="image-preview-sticky">
                <h3>Treatment Card Image</h3>
                <img src={imagePreview} alt="Treatment card" />
              </div>
            </div>

            {/* Extracted Data - Scrollable on right */}
            <div className="extracted-data-column">
              <div className="treatments-scroll-container">
                {extractedData.treatments.map((treatment, cowIndex) => (
                  <div key={cowIndex} className="treatment-card">
                    <div className="treatment-header">
                      <h4>Treatment {cowIndex + 1}</h4>
                      {treatment.matchStatus && (
                        <span className={`match-status ${treatment.matchStatus}`}>
                          {treatment.matchStatus === 'matched' ? '✓ Cow Matched' : '⚠ Not Found'}
                        </span>
                      )}
                    </div>

                    <div className="treatment-fields">
                      <div className="field-group">
                        <label>Cow Number (from image):</label>
                        <input
                          type="text"
                          value={treatment.cowNumber || ''}
                          onChange={(e) => handleUpdateTreatment(cowIndex, 'cowNumber', e.target.value)}
                        />
                      </div>

                      {treatment.lifeNumber && (
                        <div className="field-group">
                          <label>Life Number (matched):</label>
                          <input
                            type="text"
                            value={treatment.lifeNumber || ''}
                            readOnly
                            className="readonly-field"
                          />
                        </div>
                      )}

                      {treatment.calvingDate && (
                        <div className="field-group">
                          <label>Calving Date:</label>
                          <input
                            type="text"
                            value={treatment.calvingDate || ''}
                            readOnly
                            className="readonly-field"
                          />
                        </div>
                      )}

                      <div className="field-group">
                        <label>Treatment Date:</label>
                        <input
                          type="text"
                          value={treatment.date || ''}
                          onChange={(e) => handleUpdateTreatment(cowIndex, 'date', e.target.value)}
                          placeholder="DD/MM/YYYY"
                        />
                      </div>

                      <div className="field-group">
                        <label>Observations:</label>
                        <textarea
                          value={treatment.observations || ''}
                          onChange={(e) => handleUpdateTreatment(cowIndex, 'observations', e.target.value)}
                          rows="2"
                        />
                      </div>

                      <div className="products-section">
                        <label>Products Used:</label>
                        <div className="products-grid">
                          {(treatment.productMarks || []).map((mark, productIndex) => (
                            <div key={productIndex} className="product-checkbox">
                              <input
                                type="checkbox"
                                id={`product-${cowIndex}-${productIndex}`}
                                checked={mark.is_marked}
                                onChange={(e) => handleUpdateProductMark(cowIndex, productIndex, e.target.checked)}
                              />
                              <label htmlFor={`product-${cowIndex}-${productIndex}`}>
                                {mark.product_name}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="treatment-actions">
                  <button
                    className="add-treatment-btn"
                    onClick={handleAddTreatment}
                    type="button"
                  >
                    + Add Treatment Row
                  </button>
                  <button
                    className="save-btn"
                    onClick={handleSave}
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save All Treatments'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TreatmentDigitizer;
